/**
 * The left rail. Collapsible, dark, sectioned.
 *
 * Collapse state persists in localStorage because it is a preference about
 * this browser, not about the account — putting it on the server would mean a
 * round trip to render the chrome.
 */
import {
  Activity, BookOpen, Building2, ChevronLeft, Code2, Crosshair, Database,
  Film, FlaskConical, Focus, Gauge, Grid3x3, Layers, Link2, ListPlus, LogOut,
  Menu, Notebook, RefreshCw, Scissors, Search, Upload, Users, Wrench,
  type LucideIcon,
} from 'lucide-react'
import { Fragment, useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { signOut } from '../../lib/analytics'
import { useLoadNewBuildOnNavigate, useNewVersion } from '../../lib/newVersion'
import { API_BASE, type Session } from '../../lib/api'
import { ThemeToggle } from '../../theme/ThemeToggle'
import NotificationsBell from './NotificationsBell'
import OrgSwitcher from './OrgSwitcher'
import { stopImpersonating } from './stopImpersonating'
import { buildNav, isNavActive, type NavItem } from './nav'
import './sidebar.css'

const ICONS: Record<string, LucideIcon> = {
  flask: FlaskConical, crosshair: Crosshair, wrench: Wrench, focus: Focus,
  database: Database, users: Users, building: Building2, activity: Activity,
  gauge: Gauge, pulse: Activity, grid: Grid3x3, layers: Layers,
  list: ListPlus, scissors: Scissors, film: Film,
  notebook: Notebook, search: Search, code: Code2, upload: Upload,
  link: Link2,
}

const STORAGE_KEY = 'priori.sidebar.collapsed'

function ItemLink({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const { pathname } = useLocation()
  const Icon = ICONS[item.icon] ?? Grid3x3
  const active = isNavActive(item, pathname)
  const className = `rail-item${active ? ' is-active' : ''}`
    + `${item.struck ? ' is-struck' : ''}${item.pending ? ' is-pending' : ''}`
  const inner = (
    <>
      <Icon size={18} strokeWidth={1.75} aria-hidden />
      <span className="rail-item__label">{item.label}</span>
    </>
  )
  // Nothing serves this path yet. Rendered as a non-link so it cannot take
  // you somewhere unrelated, and titled so hovering explains why.
  if (item.pending) {
    return (
      <span className={className} title={`${item.label} — not built yet`}
            aria-disabled="true">
        {inner}
        <span className="rail-item__soon">soon</span>
      </span>
    )
  }
  // Not yet ported: a full-page navigation to a path this origin proxies to
  // Flask, which renders it with its own chrome. Same origin, so the URL bar
  // stays on the frontend and the session cookie needs no special handling.
  if (!item.react) {
    const href = item.href.startsWith('http') ? item.href : `${API_BASE}${item.href}`
    return (
      <a className={className} href={href} title={collapsed ? item.label : undefined}
         {...(item.newTab ? { target: '_blank', rel: 'noopener' } : {})}>
        {inner}
      </a>
    )
  }
  return (
    <Link className={className} to={item.href} title={collapsed ? item.label : undefined}>
      {inner}
    </Link>
  )
}

export default function Sidebar({ session }: { session: Session | null }) {
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(STORAGE_KEY) === '1',
  )
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0')
  }, [collapsed])
  // Deliberately NOT persisted: an interruption, not a preference.
  const [signingOut, setSigningOut] = useState(false)
  // Phones (<= 640 px, sidebar.css): the rail is an off-canvas drawer. It
  // closes on every navigation — a tapped link must land on the page,
  // not leave the drawer covering it.
  const [mobileOpen, setMobileOpen] = useState(false)
  const { pathname, search } = useLocation()
  useEffect(() => { setMobileOpen(false) }, [pathname, search])

  const newVersion = useNewVersion()
  // a newer build is out: the next click lands on it, not on this old one
  useLoadNewBuildOnNavigate(newVersion, pathname + search)
  const sections = buildNav(session)
  const isStaff = !!session?.user.is_staff || !!session?.user.is_platform_admin

  return (
    <>
    <button type="button" className="app-menu-btn"
            aria-label="Open navigation" aria-expanded={mobileOpen}
            onClick={() => setMobileOpen(true)}>
      <Menu size={20} aria-hidden />
    </button>
    {mobileOpen && (
      <div className="app-drawer-backdrop" aria-hidden
           onClick={() => setMobileOpen(false)} />
    )}
    {/* An open drawer always shows labels: the icon rail is a desktop
        preference, and a phone has the width for names while it is open. */}
    <aside className={`app-sidebar${collapsed && !mobileOpen ? ' is-collapsed' : ''}`
                      + `${mobileOpen ? ' is-mobile-open' : ''}`}>
      {/* WHOSE APP THIS IS, at a glance.
          A club user sees their club and nothing else. Priori staff see
          Priori — and, once they pick a club, the club to the RIGHT of it,
          because "I am staff, currently looking at Sporting KC" is two facts
          and showing only the second is how someone acts on the wrong club. */}
      <div className="sidebar-brand">
        {isStaff ? (
          <>
            <img className="sidebar-brand__priori"
                 src="/static/images/priori-logo-white.webp"
                 srcSet="/static/images/priori-logo-white@2x.webp 2x" alt="Priori" />
            {/* Shown only when collapsed: the wordmark is a smear at 60px. */}
            <img className="sidebar-brand__star"
                 src="/static/images/priori_star_3x.png" alt="Priori" />
            {session?.org && (
              <>
                <span className="sidebar-brand__sep" aria-hidden />
                {session.branding.team_logo_url
                  ? <img className="sidebar-brand__club"
                         src={session.branding.team_logo_url} alt="" />
                  : <span className="sidebar-brand__mark" aria-hidden />}
                <span className="sidebar-brand__name">{session.org.name}</span>
              </>
            )}
          </>
        ) : (
          <>
            {session?.branding.team_logo_url
              ? <img className="sidebar-brand__club"
                     src={session.branding.team_logo_url} alt="" />
              : <span className="sidebar-brand__mark" aria-hidden />}
            <span className="sidebar-brand__name">
              {session?.branding.team_name ?? session?.org?.name ?? 'Priori'}
            </span>
          </>
        )}
      </div>

      {/* A newer frontend has been deployed than this tab is running: say so
          at the top, in orange, and refresh only when asked (unsaved work).
          Icon only when the rail is collapsed. */}
      {newVersion && (
        <button type="button" className="rail-update"
                title="A newer version of Priori is available — refresh to load it"
                onClick={() => window.location.reload()}>
          <RefreshCw size={16} aria-hidden />
          <span className="rail-item__label">New version · Refresh</span>
        </button>
      )}

      {/* The way back, at the TOP of the rail while viewing as someone —
          the footer copy sits below every section and was easy to miss.
          Icon only when the rail is collapsed. */}
      {session?.impersonating && (
        <button type="button" className="rail-imp-exit"
                title={`Viewing as ${session.impersonating.email} — return to my account`}
                onClick={() => void stopImpersonating()}>
          <LogOut size={16} aria-hidden />
          <span className="rail-item__label">Return to my account</span>
        </button>
      )}

      <nav className="sidebar-nav">
        {sections.map((section) => (
          <div key={section.title}
               className={`rail-section${section.club
                 ? ' rail-section--plain' : ''}`}>
            {/* The switcher sits directly above the club's own section, which
                is what it changes. Above the whole rail it would look like it
                changed the platform sections too. */}
            {section.club && session && (
              <OrgSwitcher session={session} />
            )}
            <div className="rail-heading">{section.title}</div>
            {section.items.map((item, i) => (
              <Fragment key={item.label}>
                {item.group && item.group !== section.items[i - 1]?.group && (
                  /* Sub-group heading (Review / Analysis / Setup): rendered
                     above the first item of each group, invisible to the
                     collapsed rail the same way section headings are. */
                  <div className="rail-subheading">{item.group}</div>
                )}
                <ItemLink item={item} collapsed={collapsed} />
              </Fragment>
            ))}
          </div>
        ))}
      </nav>

      <button className="rail-collapse" onClick={() => setCollapsed((c) => !c)}
              aria-expanded={!collapsed} aria-label="Collapse navigation">
        <ChevronLeft size={16} className="rail-collapse__chevron" aria-hidden />
        <span className="rail-item__label">Collapse</span>
      </button>

      {session && (
        <div className="sidebar-foot">
          <div className="sidebar-foot__who">
            <span className="sidebar-foot__email" title={session.user.email}>
              {session.user.email}
            </span>
            <span className="sidebar-foot__org">{session.org?.name ?? '—'}</span>
          </div>
          {/* Stop impersonating — visible even when the banner is hidden
              (club-user impersonation hides the banner for a clean demo
              view, but there must still be an exit). */}
          {session.impersonating && (
            <button type="button" className="sidebar-foot__stop-imp"
                    title="Return to my account"
                    onClick={() => void stopImpersonating()}>
              <LogOut size={14} className="sidebar-foot__stop-imp-icon" aria-hidden />
              <span className="sidebar-foot__stop-imp-label">Return to my account</span>
            </button>
          )}
          {/* Bell, glossary, sign out — the three things that belong at the
              bottom rather than in the rail: one is a state, one is a
              reference you dip into, and one ends the session. */}
          <div className="sidebar-foot__actions">
            <NotificationsBell />
            <ThemeToggle />
            <a className="sidebar-foot__icon" href="/glossary/" title="Glossary">
              <BookOpen size={16} aria-hidden />
            </a>
            <button type="button" className="sidebar-foot__icon"
                    title="Sign out" aria-label="Sign out"
                    onClick={() => setSigningOut(true)}>
              <LogOut size={16} aria-hidden />
            </button>
          </div>
        </div>
      )}

      {signingOut && <SignOutConfirm onCancel={() => setSigningOut(false)} />}
    </aside>
    </>
  )
}

/**
 * A speed bump on the way out.
 *
 * WHY IT EXISTS. Sign out was a bare icon with no label, sitting between a
 * notification bell and a glossary link — three 28px targets in a row, two of
 * which are harmless and one of which ends the session. Clicking the wrong one
 * costs a re-authentication and whatever was half-typed on the page.
 *
 * NOT A window.confirm. The rest of the app confirms with a scrim and a panel;
 * a native dialog looks like the browser rather than the product, cannot be
 * styled, and cannot be tested through the DOM.
 *
 * CANCEL TAKES FOCUS, not the confirm. The whole point is to interrupt a
 * reflex — autofocusing "Sign out" would let a second stray Enter through the
 * bump this exists to create. Escape cancels for the same reason.
 *
 * A REAL NAVIGATION, not fetch(). /auth/logout clears the cookie server-side
 * and answers a redirect; the browser has to follow it for the session to
 * actually end and for the app to land signed-out.
 */
function SignOutConfirm({ onCancel }: { onCancel: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel])

  return (
    <div className="signout-scrim" role="dialog" aria-modal="true"
         aria-label="Sign out" onClick={onCancel}>
      {/* The panel swallows the click so hitting it does not dismiss. */}
      <div className="panel signout-confirm" onClick={(e) => e.stopPropagation()}>
        <h3 className="signout-confirm__title">Sign out?</h3>
        <p className="adm-note">
          You will need to sign in again to get back in. Anything unsaved on
          this page is lost.
        </p>
        <div className="signout-confirm__actions">
          <button type="button" className="adm-btn" autoFocus onClick={onCancel}>
            Stay signed in
          </button>
          <button type="button" className="adm-btn adm-btn--danger"
                  onClick={() => signOut(`${API_BASE}/auth/logout`)}>
            Sign out
          </button>
        </div>
      </div>
    </div>
  )
}
